import {
	DeleteOutlined,
	EditOutlined,
	PlusOutlined,
	SaveOutlined,
	TagOutlined,
} from "@ant-design/icons";
import {
	Button,
	Card,
	Col,
	Divider,
	Form,
	Modal,
	Row,
	Select,
	Spin,
	Table,
	Tag,
} from "antd";
import dayjs from "dayjs";
import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { getAperturaActiva } from "../../services/cajaEstadoService";
import { getAllCajas } from "../../services/cajaService";
import { getValidCampaignsForDate } from "../../services/campaignService";
import { getClientAddressesByNo } from "../../services/clientAddressService";
import { getAllClients, addClient } from "../../services/clientService";
import { getAllColegios } from "../../services/colegioService";
import { getValidCouponDetailsForInvoice } from "../../services/couponValidationService";
import { getTPVByNo } from "../../services/tpvService";
import {
	addInvoicePaymentLine,
	deleteInvoicePaymentLine,
	getInvoicePaymentLines,
	updateInvoicePaymentLine,
} from "../../services/invoicePaymentService";
import {
	addInvoice,
	addInvoiceLine,
	convertDraftToInvoice,
	getInvoiceLines,
	saveInvoiceAsDraft,
	updateInvoice,
} from "../../services/invoiceService";
import { hasProductLines } from "../../services/productLinesService";
import { getAllProducts } from "../../services/productService";
import { getAllTurnos } from "../../services/turnoService";
import { syncClientById } from "../../services/clientSyncService";
import { syncGruposIVA } from "../../services/grupoIVAService";
import ClientForm from "../Client/ClientForm";
import InvoiceLines from "./InvoiceLines";
import InvoicePaymentLineForm from "./InvoicePaymentLineForm";
import "./InvoiceStyles.css";
import { calculateInvoiceTotalsHelper } from "../../utils/invoiceTotals";
import { useConfig } from "../../contexts/ConfigContext";
import { formatNumberByCountry } from "../../utils/formatNumbers";
import { getInvoiceFormConfig } from "./configs/invoiceForm.config";
import { updateClient } from "../../services/clientService";
import { useCountryExtension } from "./extensions/useCountryExtension";
import { showAntdFormError, toReadableText } from "../../utils/common";
import { COUNTRY_CODES } from "../../constants/countries";

const parseDMYDate = (dateString) => {
	if (!dateString) return null;
	const parts = dateString.split("/");
	if (parts.length !== 3) return null;

	let [day, month, year] = parts.map((p) => parseInt(p, 10));

	if (isNaN(day) || isNaN(month) || isNaN(year)) return null;

	// Normaliza años 2 dígitos → 20XX
	if (year < 100) year += year <= 30 ? 2000 : 1900;

	return new Date(year, month - 1, day);
};

const InvoiceForm = ({ invoice, onSave, onCancel }) => {
	const [form] = Form.useForm();
	const [products, setProducts] = useState();
	const [clients, setClients] = useState();
	const [clientAddresses, setClientAddresses] = useState([]);
	const [colegios, setColegios] = useState();
	const [cajas, setCajas] = useState();
	const [turnos, setTurnos] = useState();
	const [lines, setLines] = useState([]);
	const [paymentLines, setPaymentLines] = useState([]);
	const [selectedPaymentLine, setSelectedPaymentLine] = useState(null);
	const [paymentLineModalVisible, setPaymentLineModalVisible] = useState(false);
	const [loading, setLoading] = useState(false);
	const [lineLoading, setLineLoading] = useState(false);
	const [paymentLineLoading, setPaymentLineLoading] = useState(false);
	const [selectedClientDiscount, setSelectedClientDiscount] = useState(0);
	const [confirmationModalVisible, setConfirmationModalVisible] =
		useState(false);
	const [confirmationData, setConfirmationData] = useState({});
	const [confirmationClientData, setConfirmationClientData] = useState(null);
	const [ticketModalVisible, setTicketModalVisible] = useState(false);
	const [createdInvoice, setCreatedInvoice] = useState(null);
	const [clientModalVisible, setClientModalVisible] = useState(false);
	const [isNewClient, setIsNewClient] = useState(false);
	const [aperturaActiva, setAperturaActiva] = useState(null);
	const [cajaActiva, setCajaActiva] = useState(null);
	const [tpv, setTpv] = useState(null);
	const [turnoNombre, setTurnoNombre] = useState("");
	const [availableCouponDetails, setAvailableCouponDetails] = useState([]);
	const [selectedCouponDetail, setSelectedCouponDetail] = useState(null);
	const [loadingCoupons, setLoadingCoupons] = useState(false);
	const [campaigns, setCampaigns] = useState([]);
	const [loadingCampaigns] = useState(false);
	const loadedCampaigns = useRef(false);
	const submittingRef = useRef(false);
	const [initialDataLoaded, setInitialDataLoaded] = useState(false);
	const [hasKitsInLines, setHasKitsInLines] = useState(false);
	const [loadingClientForm, setLoadingClientForm] = useState(false);
	const [error, setError] = useState("");
	const { country, loading: configLoading } = useConfig();
	const defaultDocumentType = useMemo(
		() => (country?.code === COUNTRY_CODES.ELSALVADOR ? "NIT" : "Cedula"),
		[country?.code]
	);

	const { requiredFields, permissions } = getInvoiceFormConfig(country?.code);
	const selectedClientNo = Form.useWatch("Sell_to_Customer_No_", form);
	const selectedColegio = Form.useWatch("Colegio", form);
	const selectedCampaignNo = Form.useWatch("Campaign_No_", form);
	const selectedCampaign = useMemo(() => {
		if (!selectedCampaignNo || !campaigns?.length) return null;
		return campaigns.find((c) => c.No === selectedCampaignNo) || null;
	}, [selectedCampaignNo, campaigns]);
	const selectedClient = useMemo(() => {
		if (!clients || !selectedClientNo) return null;
		return clients.find(
			(c) => c.id === selectedClientNo || c.No === selectedClientNo
		);
	}, [clients, selectedClientNo]);

	// Inicializo extension por pais. Obtenemos los componentes "slot" y el hook de validación para el país actual.
	const {
		submitInvoiceAndPrepareFiscalTicket,
		useConfirmationModal,
		InvoiceHeaderSlot,
		ClientSectionSlot,
		BillingInfoSectionSlot,
		ConfirmationSectionSlot,
		TicketSectionSlot
	} = useCountryExtension();

	const confirmationModal = useConfirmationModal(lines, selectedClient);

	const closeConfirmationModal = useCallback(() => {
		setConfirmationModalVisible(false);
		setConfirmationClientData(null);
	}, []);

	// Observar cambios en el tipo de documento para renderizado condicional
	const documentType = Form.useWatch("Document_Type", form);
	const isInvoiceEditing = !!invoice?.id;
	const isSynchronized = !!invoice?.No;
	const isDraft = invoice?.isDraft || false;
	const isNewCreditMemo = !invoice?.id && invoice?.Document_Type === "Credit Memo";

	// Función para determinar si una campaña está activa
	const isCampaignActive = useCallback((campaign) => {
		const today = new Date();

		// Si no hay fechas definidas, se considera activa
		if (!campaign.Starting_Date && !campaign.Ending_Date) {
			return true;
		}

		// Verificar si está en el rango de fechas
		let startDate = null;
		let endDate = null;

		if (campaign.Starting_Date) {
			startDate = parseDMYDate(campaign.Starting_Date);
		}

		if (campaign.Ending_Date) {
			endDate = parseDMYDate(campaign.Ending_Date);
		}

		if (startDate && today < startDate) return false;
		if (endDate && today > endDate) return false;

		return true;
	}, []);

	// Función para seleccionar la campaña por defecto
	const selectDefaultCampaign = useCallback(
		(campaignList) => {
			if (!campaignList || campaignList.length === 0) return;

			// Filtrar campañas activas
			const activeCampaigns = campaignList.filter((campaign) =>
				isCampaignActive(campaign)
			);

			if (activeCampaigns.length > 0) {
				// Ordenar campañas por fecha de inicio (más reciente primero)
				const sortedCampaigns = activeCampaigns.sort((a, b) => {
					const dateA = parseDMYDate(a.Starting_Date) || new Date(0);
					const dateB = parseDMYDate(b.Starting_Date) || new Date(0);

					// Ordenar de más reciente a más antigua (descendente)
					return dateB.getTime() - dateA.getTime();
				});

				// Tomar la campaña más reciente (primera en la lista ordenada)
				const defaultCampaign = sortedCampaigns[0];

				// Solo establecer si no hay una campaña ya seleccionada y no estamos editando una factura
				if (!form.getFieldValue("Campaign_No_") && !isInvoiceEditing) {
					form.setFieldValue("Campaign_No_", defaultCampaign.No);
					console.log(
						`Campaña seleccionada por defecto (más reciente): ${defaultCampaign.No
						} - ${defaultCampaign.Description || "Sin descripción"} (Inicio: ${defaultCampaign.Starting_Date || "Sin fecha"
						})`
					);
				}
			}
		},
		[form, isCampaignActive, isInvoiceEditing]
	);

	// Función optimizada para cargar todos los datos iniciales en paralelo
	const loadInitialData = useCallback(async () => {
		if (initialDataLoaded) return;

		try {
			setLoading(true);
			console.log("Iniciando carga de datos iniciales...");

			// Cargar todos los datos en paralelo
			const [
				clientList,
				colegioList,
				cajaList,
				turnoList,
				productList,
				apertura,
				validCampaigns,
			] = await Promise.all([
				getAllClients(),
				getAllColegios(),
				getAllCajas(),
				getAllTurnos(),
				getAllProducts(),
				getAperturaActiva(),
				getValidCampaignsForDate(),
			]);

			console.log("Datos cargados exitosamente");

			// Actualizar todos los estados de una vez
			setClients(clientList);
			setColegios(colegioList);
			setCajas(cajaList);
			setTurnos(turnoList);
			setProducts(productList);
			setAperturaActiva(apertura);
			setCampaigns(validCampaigns);
			loadedCampaigns.current = true;

			// Seleccionar campaña por defecto basada en fecha actual
			selectDefaultCampaign(validCampaigns);

			// Configurar datos de caja y turno
			if (apertura && cajaList && turnoList) {
				const cajaSel = cajaList.find((c) => c.No === apertura.Caja);
				const turnoSel = turnoList.find(
					(t) =>
						t.No === apertura.Turno || t.DescripcionTurno === apertura.Turno
				);

				setCajaActiva(cajaSel);

				const tpv = cajaSel && cajaSel.TPV ? await getTPVByNo(cajaSel.TPV) : null;
				setTpv(tpv);
				setTurnoNombre(
					turnoSel ? turnoSel.DescripcionTurno : apertura.Turno || ""
				);

				// Actualizar el formulario con la caja y turno actuales
				form.setFieldsValue({
					caja: apertura.Caja,
					turno: turnoSel ? turnoSel.No : apertura.Turno,
				});
			} else if (!apertura) {
				window.messageApi.warning(
					"No hay una caja abierta. Debe abrir una caja para crear facturas."
				);
			}

			setInitialDataLoaded(true);
			console.log("Carga inicial completada");
		} catch (error) {
			console.error("Error al cargar datos iniciales:", error);
			window.messageApi.error(
				"Error al cargar datos. Por favor, inténtelo de nuevo."
			);
		} finally {
			setLoading(false);
		}
	}, [initialDataLoaded, form, selectDefaultCampaign]);

	// Cargar datos iniciales solo una vez
	useEffect(() => {
		loadInitialData();
	}, [loadInitialData]);

	const hasSpecialDiscount = useMemo(() => {
		return lines.some(line => (line.Line_Discount_ || 0) > 0);
	}, [lines]);

	const totals = useMemo(
		() => calculateInvoiceTotalsHelper({ lines }, paymentLines, country),
		[lines, paymentLines, country]
	);

	const {
		subtotal,
		vat,
		total,
		baseTotal,
		totalAjustado,
		ajuste,
		paymentTotal,
		pending,
		paymentMethodDiscount,
	} = totals;

	const loadValidCoupons = async () => {
		try {
			if (!cajas || !aperturaActiva || !tpv || !clients) {
				return;
			}

			setLoadingCoupons(true);

			const formValues = form.getFieldsValue();
			const invoiceData = {
				caja: formValues.caja || aperturaActiva?.Caja,
				Colegio: formValues.Colegio,
				...formValues,
			};

			const { allCouponDetails } = await getValidCouponDetailsForInvoice(
				invoiceData,
				selectedClient,
				tpv
			);

			setAvailableCouponDetails(allCouponDetails);
		} catch (error) {
			console.error("Error al cargar cupones válidos:", error);
			window.messageApi.error("Error al cargar cupones disponibles");
		} finally {
			setLoadingCoupons(false);
		}
	};


	// Cargar cupones válidos cuando estén disponibles todos los datos necesarios
	useEffect(() => {
		if (
			initialDataLoaded &&
			clients &&
			cajas &&
			aperturaActiva &&
			tpv !== null &&
			!invoice?.Cupon
		) {
			loadValidCoupons();
		}
	}, [
		initialDataLoaded,
		clients,
		cajas,
		aperturaActiva,
		tpv,
		invoice?.Cupon,
		selectedClientNo,
		selectedColegio
	]);

	// Función para verificar si hay kits en las líneas de factura
	const checkForKitsInLines = useCallback(async () => {
		if (!lines || lines.length === 0) {
			setHasKitsInLines(false);
			return;
		}

		try {
			let foundKit = false;

			for (const line of lines) {
				if (line.No && line.key !== "new") {
					const hasLines = await hasProductLines(line.No);
					if (hasLines) {
						foundKit = true;
						break;
					}
				}
			}

			setHasKitsInLines(foundKit);

			// Si encontramos un kit y no es una factura en edición, activar automáticamente el checkbox
			if (foundKit && !isInvoiceEditing) {
				form.setFieldValue("libros_sueltos", true);
			}
		} catch (error) {
			console.error("Error al verificar kits en líneas:", error);
			setHasKitsInLines(false);
		}
	}, [lines, form, isInvoiceEditing]);

	// Verificar kits cada vez que cambien las líneas
	useEffect(() => {
		checkForKitsInLines();
	}, [checkForKitsInLines]);

	// useEffect para cargar líneas de factura y pago cuando se edita una factura existente (optimizado)
	useEffect(() => {
		const loadInvoiceData = async () => {
			if (!isInvoiceEditing || !invoice?.id) return;

			try {
				setLineLoading(true);
				setPaymentLineLoading(true);

				// Cargar líneas de factura y pago en paralelo
				const [lineList, paymentLineList] = await Promise.all([
					getInvoiceLines(invoice.id),
					getInvoicePaymentLines(invoice),
				]);

				setLines(lineList);

				//Si hay descuento en productos, no aplicar descuento en pagos
				let lineWithDiscount = lineList.some(line => (line.Line_Discount_ || 0) > 0);

				const loadedPaymentLines = paymentLineList.map((line) => {
					return {
						...line,
						Amount: lineWithDiscount ? (line.rawAmount ?? line.Amount) : line.Amount,
						Dto: lineWithDiscount ? 0 : line.Dto,
						Dto_Amount: lineWithDiscount ? 0 : line.Dto_Amount,

					}
				});

				setPaymentLines(loadedPaymentLines);
			} catch (error) {
				console.error("Error al cargar datos de la factura:", error);
				window.messageApi.error(
					"No se pudieron cargar los datos de la factura"
				);
			} finally {
				setLineLoading(false);
				setPaymentLineLoading(false);
			}
		};

		loadInvoiceData();
	}, [isInvoiceEditing, invoice?.id]);

	// useEffect para configurar líneas predefinidas en facturas nuevas (ej: abonos)
	useEffect(() => {
		if (!isInvoiceEditing && invoice?.lines) {
			setLines(invoice.lines);
		}
		if (!isInvoiceEditing && invoice?.paymentLines) {
			let lineWithDiscount = invoice?.lines.some(line => (line.Line_Discount_ || 0) > 0);
			let paymentLineList = invoice?.paymentLines.map((line) => {
				return {
					...line,
					id: null,
					invoiceId: null,
					Document_No_: null,
					Amount: lineWithDiscount ? (line.originalAmount ?? line.Amount) : line.Amount,
					Dto: lineWithDiscount ? 0 : line.Dto,
					Dto_Amount: lineWithDiscount ? 0 : line.Dto_Amount,
				};
			});
			setPaymentLines(paymentLineList);
		}
	}, [isInvoiceEditing, invoice?.lines, invoice?.paymentLines]);

	// useEffect para configurar datos básicos del formulario y cliente (optimizado)
	useEffect(() => {
		if (!invoice) return;

		const formattedInvoice = {
			...invoice,
			// Para notas de crédito nuevas, usar siempre fechas actuales
			// Para otras facturas, usar las fechas existentes o actuales si no hay
			Posting_Date: isNewCreditMemo ? dayjs() : (invoice.Posting_Date
				? dayjs(invoice.Posting_Date)
				: dayjs()),
			Document_Date: isNewCreditMemo ? dayjs() : (invoice.Document_Date
				? dayjs(invoice.Document_Date)
				: dayjs()),
			Document_Type: invoice.Document_Type || "Invoice",
			caja: invoice.caja || "",
			turno: invoice.turno || "",
		};

		form.setFieldsValue(formattedInvoice);

		// Configurar descuento del cliente si está disponible
		if (invoice.Sell_to_Customer_No_ && clients) {

			if (selectedClient) {
				setSelectedClientDiscount(selectedClient.POS_Discount || 0);
				setIsNewClient(!selectedClient.No);

				// Asegurar que los datos del cliente estén en el formulario, incluyendo Direccion_2
				form.setFieldsValue({
					Bill_to_Name: invoice.Bill_to_Name || selectedClient.name,
					Bill_to_Address: invoice.Bill_to_Address || selectedClient.Direccion || "",
					Direccion_2: invoice.Direccion_2 || selectedClient.Direccion_2 || "",
					Bill_to_City: invoice.Bill_to_City || selectedClient.Poblacion || "",
					VAT_Registration_No_: invoice.VAT_Registration_No_ || selectedClient.Numero_Identificacion_fiscal || "",
					Bill_to_Country_Region_Code: invoice.Bill_to_Country_Region_Code || selectedClient.Pais || "",
					Tipo_Documento_SrI: invoice.Tipo_Documento_SrI || selectedClient.Tipo_Documento_SrI || defaultDocumentType,
					E_Mail: invoice.E_Mail || selectedClient.E_Mail || "",
					Telefono: invoice.Telefono || selectedClient.Telefono || "",
					Mobil: invoice.Mobil || selectedClient.Mobil || "",
				});

				// Cargar direcciones del cliente si tiene No (está sincronizado)
				if (selectedClient.No) {
					getClientAddressesByNo(selectedClient.No)
						.then((addresses) => {
							setClientAddresses(addresses);

							// Si es una factura nueva y solo tiene una dirección, seleccionarla automáticamente
							// Para facturas existentes, mantener la dirección ya seleccionada
							if (!isInvoiceEditing && addresses.length === 1) {
								form.setFieldValue("Ship_to_Code", addresses[0].Code);
							}
						})
						.catch((error) => {
							console.error("Error al cargar direcciones del cliente:", error);
							setClientAddresses([]);
						});
				} else {
					setClientAddresses([]);
				}
			}
		}
	}, [invoice, clients, form, isInvoiceEditing]);

	// useEffect para configurar cupón existente en facturas editadas (optimizado)
	useEffect(() => {
		const setupExistingCoupon = async () => {
			if (!invoice?.Cupon || !invoice?.Tipo_Detalle || !invoice?.No_Detalle)
				return;
			if (!initialDataLoaded || !clients || !cajas || !aperturaActiva) return;

			try {
				const detailKey = `${invoice.Cupon}-${invoice.Tipo_Detalle}-${invoice.No_Detalle}`;

				// Si ya tenemos el cupón configurado, no hacer nada
				if (selectedCouponDetail?.key === detailKey) return;

				const invoiceData = {
					caja: invoice.caja || aperturaActiva?.Caja,
					Colegio: invoice.Colegio,
					...invoice,
				};

				const { allCouponDetails } = await getValidCouponDetailsForInvoice(
					invoiceData,
					selectedClient,
					tpv
				);
				const specificCouponDetail = allCouponDetails.find(
					(c) => c.key === detailKey
				);

				const couponDetail = specificCouponDetail || {
					key: detailKey,
					couponNo: invoice.Cupon,
					detailType: invoice.Tipo_Detalle,
					detailNo: invoice.No_Detalle,
					isValid: false,
					originalCoupon: { Line_Discounts: [] },
				};

				setSelectedCouponDetail(couponDetail);
				form.setFieldsValue({
					couponDetail: detailKey,
					Cupon: invoice.Cupon,
					Tipo_Detalle: invoice.Tipo_Detalle,
					No_Detalle: invoice.No_Detalle,
				});
			} catch (error) {
				console.warn("Error al cargar información del cupón:", error);
				const detailKey = `${invoice.Cupon}-${invoice.Tipo_Detalle}-${invoice.No_Detalle}`;
				setSelectedCouponDetail({
					key: detailKey,
					couponNo: invoice.Cupon,
					detailType: invoice.Tipo_Detalle,
					detailNo: invoice.No_Detalle,
					isValid: false,
					originalCoupon: { Line_Discounts: [] },
				});

				form.setFieldsValue({
					couponDetail: detailKey,
					Cupon: invoice.Cupon,
					Tipo_Detalle: invoice.Tipo_Detalle,
					No_Detalle: invoice.No_Detalle,
				});
			}
		};

		// Solo ejecutar si es una factura con cupón existente y todos los datos están cargados
		if (
			invoice?.Cupon &&
			initialDataLoaded &&
			clients &&
			cajas &&
			aperturaActiva &&
			tpv !== null
		) {
			setupExistingCoupon();
		}
	}, [
		invoice,
		initialDataLoaded,
		clients,
		cajas,
		aperturaActiva,
		tpv,
		selectedCouponDetail?.key,
		form,
	]);

	// Todos los hooks deben ir antes del return condicional
	const handleClientChange = useCallback(
		async (value) => {
			const client = clients.find((c) => c.id === value);
			if (client) {
				form.setFieldsValue({
					Bill_to_Name: client.name,
					Bill_to_Address: client.Direccion || "",
					Direccion_2: client.Direccion_2 || "",
					Bill_to_City: client.Poblacion || "",
					VAT_Registration_No_: client.Numero_Identificacion_fiscal || "",
					Bill_to_Country_Region_Code: client.Pais || tpv?.pais || "",
					Tipo_Documento_SrI: client.Tipo_Documento_SrI || defaultDocumentType,
					E_Mail: client.E_Mail || "",
					Telefono: client.Telefono || "",
					Mobil: client.Mobil || "",
					Ship_to_Code: "", // Limpiar dirección de envío al cambiar cliente
				});
				// Guardar el descuento del cliente seleccionado
				setSelectedClientDiscount(client.POS_Discount || 0);
				// Si es un cliente nuevo sin sincronizar, marcarlo
				setIsNewClient(!client.No);

				// Cargar direcciones del cliente si tiene No (está sincronizado)
				if (client.No) {
					try {
						const addresses = await getClientAddressesByNo(client.No);
						setClientAddresses(addresses);

						// Si solo tiene una dirección, seleccionarla automáticamente
						if (addresses.length === 1) {
							form.setFieldValue("Ship_to_Code", addresses[0].Code);
						}
					} catch (error) {
						console.error("Error al cargar direcciones del cliente:", error);
						setClientAddresses([]);
					}
				} else {
					setClientAddresses([]);
				}

				setAvailableCouponDetails([]);
				setSelectedCouponDetail(null);

				// Limpiar campos de cupón
				form.setFieldsValue({
					couponDetail: null,
					Cupon: null,
					Tipo_Detalle: null,
					No_Detalle: null,
				});
			}
		},
		[clients, form, tpv]
	);

	// Función para manejar cambios en el colegio (recargar cupones)
	const handleColegioChange = useCallback(
		(colegioNo) => {
			form.setFieldsValue({ Colegio: colegioNo });

			// Solo resetear cupones si hay un cliente seleccionado
			if (form.getFieldValue("Sell_to_Customer_No_")) {
				setAvailableCouponDetails([]);
				setSelectedCouponDetail(null);

				// Limpiar campos de cupón
				form.setFieldsValue({
					couponDetail: null,
					Cupon: null,
					Tipo_Detalle: null,
					No_Detalle: null,
				});
			}
		},
		[form]
	);

	// Comparación con tolerancia para evitar problemas de redondeo
	const isApproximatelyZero = (value, tolerance = 0.01) => {
		return Math.abs(value) <= tolerance;
	};

	if (!initialDataLoaded || configLoading) {
		return (
			<div style={{ padding: "50px", textAlign: "center" }}>
				<Spin size="large" tip="Cargando datos del formulario..." />
			</div>
		);
	}

	// Función para manejar la creación de un nuevo cliente
	const handleAddClient = () => {
		setClientModalVisible(true);
	};

	// Función para manejar cuando se guarda un nuevo cliente
	const handleClientAdded = async (newClient) => {
		// Actualizar/reemplazar en la lista de clientes sin duplicados
		setClients((prevClients = []) => {
			const hasClient = prevClients.some((client) => client.id === newClient.id);
			if (hasClient) {
				return prevClients.map((client) =>
					client.id === newClient.id ? newClient : client
				);
			}
			return [...prevClients, newClient];
		});

		// Seleccionar el nuevo cliente en el formulario
		const clientIdentifier = newClient.id;

		form.setFieldsValue({
			...newClient,
			Sell_to_Customer_No_: clientIdentifier,
			Bill_to_Name: newClient.name,
			Bill_to_Address: newClient.Direccion || "",
			Direccion_2: newClient.Direccion_2 || "",
			Bill_to_City: newClient.Poblacion || "",
			VAT_Registration_No_: newClient.Numero_Identificacion_fiscal || "",
			Bill_to_Country_Region_Code: newClient.Pais || "",
			Tipo_Documento_SrI: newClient.Tipo_Documento_SrI || defaultDocumentType,
			E_Mail: newClient.E_Mail || "",
			Telefono: newClient.Telefono || "",
			Mobil: newClient.Mobil || "",
			Ship_to_Code: "", // Nuevo cliente no tiene direcciones
		});

		// Guardar el descuento del cliente
		setSelectedClientDiscount(newClient?.POS_Discount || 0);

		// Marcar como cliente nuevo no sincronizado solo si no tiene No
		setIsNewClient(!newClient.No);

		// Si ya está sincronizado en BC, cargar direcciones asociadas
		if (newClient.No) {
			try {
				const addresses = await getClientAddressesByNo(newClient.No);
				setClientAddresses(addresses);
				if (addresses.length === 1) {
					form.setFieldValue("Ship_to_Code", addresses[0].Code);
				}
			} catch (addressError) {
				console.error("Error al cargar direcciones del nuevo cliente:", addressError);
				setClientAddresses([]);
			}
		} else {
			setClientAddresses([]);
		}

		window.messageApi.success("Cliente añadido y seleccionado correctamente");
	};

	const handleSubmitNewClient = async (clientData) => {
		let createdAndSynced = false;
		try {
			setError("");
			setLoadingClientForm(true);

			const client = await addClient({ ...clientData, pendingSync: 1 });
			window.messageApi.success("Cliente guardado. Sincronizando con Business Central...");

			const syncResult = await syncClientById(client.id);
			const syncedClient = syncResult?.client || client;
			if (!syncResult?.ok || !syncedClient?.No) {
				throw new Error(
					"El cliente se guardó, pero no se pudo sincronizar con Business Central."
				);
			}

			const ivaSynced = await syncGruposIVA();

			await handleClientAdded(syncedClient);
			createdAndSynced = true;

			if (!ivaSynced) {
				window.messageApi.warning(
					"Cliente sincronizado, pero no se pudieron refrescar los grupos de IVA."
				);
			}

		} catch (error) {
			setError(error.message || "Error al procesar el cliente");
			console.error("Error al procesar cliente:", error);
		} finally {
			setLoadingClientForm(false);
			if (createdAndSynced) {
				setClientModalVisible(false);
			}
		}
	};

	const handleCancelNewClientForm = (form) => {
		form.resetFields();
		setClientModalVisible(false);
	};


	// Función para manejar la selección de cupón
	const handleCouponChange = (detailKey) => {
		if (!detailKey) {
			setSelectedCouponDetail(null);
			form.setFieldsValue({
				Cupon: null,
				Tipo_Detalle: null,
				No_Detalle: null,
			});
			return;
		}

		const couponDetail = availableCouponDetails.find(
			(c) => c.key === detailKey
		);
		if (couponDetail && couponDetail.isValid) {
			setSelectedCouponDetail(couponDetail);

			// Guardar los tres campos requeridos
			form.setFieldsValue({
				Cupon: couponDetail.couponNo,
				Tipo_Detalle: couponDetail.detailType,
				No_Detalle: couponDetail.detailNo,
			});

			window.messageApi.success(
				`Cupón ${toReadableText(
					couponDetail.couponNo,
					"sin número"
				)} - ${toReadableText(
					couponDetail.couponDescription,
					"sin descripción"
				)} (${toReadableText(couponDetail.detailType)}: ${toReadableText(
					couponDetail.detailNo, "Sin información"
				)}) seleccionado`
			);
		} else {
			window.messageApi.warning(
				"El detalle de cupón seleccionado no es válido para esta factura"
			);
		}
	};

	const syncPaymentLines = async ({
		paymentLines,
		invoiceId,
		invoiceNo,
		documentType,
		paymentMethodDiscount,
	}) => {
		const getPersistentPaymentLineId = (line) => {
			if (typeof line?.id === "number" && Number.isFinite(line.id)) {
				return line.id;
			}
			if (typeof line?.id === "string") {
				const parsedId = Number(line.id);
				if (Number.isFinite(parsedId)) {
					return parsedId;
				}
			}
			return null;
		};

		for (const line of paymentLines) {
			const persistentId = getPersistentPaymentLineId(line);
			const isNewLine = persistentId === null;

			const { id, ...rest } = line; // limpiamos id si hace falta

			const discountAmount =
				paymentMethodDiscount === 0
					? 0
					: (line.Amount || 0) * (paymentMethodDiscount / 100);

			const payload = {
				...rest,
				Dto: paymentMethodDiscount,
				Dto_Amount: discountAmount,
				invoiceId,
				Document_Type: documentType,
				Document_No_: invoiceNo,
			};

			if (isNewLine) {
				await addInvoicePaymentLine(payload);
			} else {
				await updateInvoicePaymentLine(persistentId, payload);
			}
		}
	};

	const handleSubmit = async (values) => {
		try {
			// Verificar que haya una apertura de caja activa
			if (!aperturaActiva && !isInvoiceEditing) {
				window.messageApi.error(
					"No hay una caja abierta. Debe abrir una caja para crear facturas."
				);
				return;
			}

			// Filtrar líneas válidas (excluyendo la fila de edición "new" si existe)
			const validLines = lines.filter(line => line.No && line.key !== 'new');

			// Validar si hay productos
			if (validLines.length === 0) {
				return window.messageApi.error("Debe seleccionar al menos un producto para generar la factura.");
			}

			// Verificar que las notas de crédito tengan pendiente 0.00 (con tolerancia)
			if (values.Document_Type === "Credit Memo") {
				const pendingAmount =
					Math.abs(totalAjustado + ajuste) -
					paymentTotal;
				if (!isApproximatelyZero(pendingAmount)) {
					window.messageApi.error(
						`No se puede crear una nota de crédito con importe pendiente. El pendiente actual es ${pendingAmount.toFixed(
							2
						)} $. Debe ajustar los pagos para que el pendiente sea exactamente 0.00.`
					);
					return;
				}
			}

			// Usar el messageApi global para mostrar mensajes
			window.messageApi.loading("Preparando confirmación...", 1);

			// Asegurarnos de que se usan los valores de la apertura activa
			if (aperturaActiva && !isInvoiceEditing) {
				values.caja = aperturaActiva.Caja;
				// Encontrar el turno correcto y usar su No
				if (turnos) {
					const turnoSel = turnos.find(
						(t) =>
							t.No === aperturaActiva.Turno ||
							t.DescripcionTurno === aperturaActiva.Turno
					);
					values.turno = turnoSel ? turnoSel.No : aperturaActiva.Turno;
				} else {
					values.turno = aperturaActiva.Turno;
				}
			}

			// Asegurar que los datos del cliente están completos, tanto en values como en el formulario de confirmación
			// Priorizar los datos del formulario (que ya están completos para notas de crédito) sobre los del cliente
			const completeValues = {
				...values,
				Tipo_Documento_SrI:
					values.Tipo_Documento_SrI ?? form.getFieldValue("Tipo_Documento_SrI"),
			};

			setConfirmationData(completeValues);
			setConfirmationClientData({
				...completeValues,
				name: completeValues.Bill_to_Name ?? "",
				Direccion: completeValues.Bill_to_Address ?? "",
				Poblacion: completeValues.Bill_to_City ?? "",
				Pais: completeValues.Bill_to_Country_Region_Code ?? "",
				Numero_Identificacion_fiscal: completeValues.VAT_Registration_No_ ?? "",
				Tipo_Documento_SrI: completeValues.Tipo_Documento_SrI ?? "",
				// Electrónica / adicionales Paraguay
				EInvNature: completeValues.EInvNature || selectedClient?.EInvNature || "",
				EInvOperationType: completeValues.EInvOperationType || selectedClient?.EInvOperationType || "",
				EInvTaxPayerType: completeValues.EInvTaxPayerType || selectedClient?.EInvTaxPayerType || "",
				EInvDocumentTypeID: completeValues.EInvDocumentTypeID || selectedClient?.EInvDocumentTypeID || "",
				EInvDocumentNoID: completeValues.EInvDocumentNoID || selectedClient?.EInvDocumentNoID || "",
				EInvHouseNo: completeValues.EInvHouseNo || selectedClient?.EInvHouseNo || "",
				EInvDepartmentCode: completeValues.EInvDepartmentCode || selectedClient?.EInvDepartmentCode || "",
				EInvDistrictCode: completeValues.EInvDistrictCode || selectedClient?.EInvDistrictCode || "",
				EInvCityCode: completeValues.EInvCityCode || selectedClient?.EInvCityCode || "",
				Salesperson_Code: completeValues.Salesperson_Code || selectedClient?.Salesperson_Code || "",
				Invoice_Disc__Code: completeValues.Invoice_Disc__Code || selectedClient?.Invoice_Disc__Code || "",
				AnotherDocument: completeValues.AnotherDocument || selectedClient?.AnotherDocument || "",
				Mobil: completeValues.Mobil || selectedClient?.Mobil || "",
				No: completeValues.No || selectedClient?.No || "",
			});

			if (confirmationModal) {
				setConfirmationModalVisible(true);
			} else {
				handleConfirmationSubmit(completeValues, false)
			}

		} catch (error) {
			console.error("Error al preparar confirmación:", error);
			window.messageApi.error("No se pudo preparar la confirmación de datos");
		}
	};

	const handleSaveAsDraft = async () => {
		try {
			// Verificar que haya una apertura de caja activa
			if (!aperturaActiva && !isInvoiceEditing) {
				window.messageApi.error(
					"No hay una caja abierta. Debe abrir una caja para crear facturas."
				);
				return;
			}

			// Filtrar líneas válidas (excluyendo la fila de edición "new" si existe)
			const validLines = lines.filter(line => line.No && line.key !== 'new');

			// Validar si hay productos
			if (validLines.length === 0) {
				return window.messageApi.error("Debe seleccionar al menos un producto para generar el borrador.");
			}

			const values = form.getFieldsValue();

			// Verificar que las notas de crédito tengan pendiente 0.00 (con tolerancia, incluso para borradores)
			if (values.Document_Type === "Credit Memo") {
				const pendingAmount =
					Math.abs(totalAjustado + ajuste) -
					paymentTotal;
				if (!isApproximatelyZero(pendingAmount)) {
					window.messageApi.error(
						`No se puede guardar una nota de crédito con importe pendiente. El pendiente actual es ${pendingAmount.toFixed(
							2
						)} $. Debe ajustar los pagos para que el pendiente sea exactamente 0.00.`
					);
					return;
				}
			}

			setLoading(true);

			// Asegurarnos de que se usan los valores de la apertura activa
			if (aperturaActiva && !isInvoiceEditing) {
				values.caja = aperturaActiva.Caja;
				// Encontrar el turno correcto y usar su No
				if (turnos) {
					const turnoSel = turnos.find(
						(t) =>
							t.No === aperturaActiva.Turno ||
							t.DescripcionTurno === aperturaActiva.Turno
					);
					values.turno = turnoSel ? turnoSel.No : aperturaActiva.Turno;
				} else {
					values.turno = aperturaActiva.Turno;
				}
			}

			// Asegurar que los datos del cliente están completos
			// Priorizar los datos del formulario (que ya están completos para notas de crédito) sobre los del cliente
			const clientData = {
				Bill_to_Name: values.Bill_to_Name || selectedClient?.name || "",
				Bill_to_Address:
					values.Bill_to_Address || selectedClient?.Direccion || "",
				Direccion_2: values.Direccion_2 || selectedClient?.Direccion_2 || "",
				Bill_to_City: values.Bill_to_City || selectedClient?.Poblacion || "",
				VAT_Registration_No_:
					values.VAT_Registration_No_ ||
					selectedClient?.Numero_Identificacion_fiscal ||
					"",
				Bill_to_Country_Region_Code:
					values.Bill_to_Country_Region_Code || selectedClient?.Pais || "",
				Tipo_Documento_SrI:
					values.Tipo_Documento_SrI ||
					selectedClient?.Tipo_Documento_SrI ||
					defaultDocumentType,
				Ship_to_Code: values.Ship_to_Code || "",
			};

			// Formatear datos de la factura
			const invoiceData = {
				...values,
				...clientData,
				Document_Type: values.Document_Type || "Invoice",
				Posting_Date: values.Posting_Date?.format("YYYY-MM-DD"),
				Document_Date: values.Document_Date?.format("YYYY-MM-DD"),
				// Para abonos, asegurarse de guardar el tipo de documento corregido
				Applies_to_Doc_Type:
					values.Document_Type === "Credit Memo" ? "Invoice" : undefined,
				// Guardar caja y turno para el ticket
				caja: values.caja,
				turno: values.turno,
				// Marcar como borrador
				isDraft: true,
			};

			let savedDraft;

			if (isInvoiceEditing) {
				// Actualizar factura existente como borrador
				savedDraft = await updateInvoice(invoice.id, invoiceData);
				// Actualiza lineas de pago del borrador
				await syncPaymentLines({
					paymentLines,
					invoiceId: invoice.id,
					invoiceNo: null,
					documentType: invoice.Document_Type,
					paymentMethodDiscount,
				});
			} else {
				// Crear nuevo borrador
				savedDraft = await saveInvoiceAsDraft(invoiceData);

				// Guardar líneas (filtrando líneas vacías e incompletas)
				const validLines = lines.filter(
					(line) =>
						line.key !== "new" && // Excluir la línea temporal
						line.No &&
						line.Description
				);

				for (const line of validLines) {
					await addInvoiceLine({
						...line,
						invoiceNo: undefined,
						invoiceId: savedDraft.id,
						Document_Type: savedDraft.Document_Type,
					});
				}

				// Guardar líneas de pago
				for (const line of paymentLines) {
					let discountAmount = paymentMethodDiscount == 0 ? 0 : line.Dto_Amount; //ya calculado en formulario linea de pago, overwrite value solo si ya tiene descuento en otro lado

					await addInvoicePaymentLine({
						...line,
						Dto: paymentMethodDiscount,
						Dto_Amount: discountAmount,
						invoiceId: savedDraft.id,
						Document_Type: savedDraft.Document_Type,
						Document_No_: savedDraft.No,
						Line_No: line.Line_No || paymentLines.indexOf(line) + 1,
					});
				}

				window.messageApi.success("Borrador guardado correctamente");
			}

			// Notificar que se ha guardado
			onSave(savedDraft);
		} catch (error) {
			console.error("Error al guardar borrador:", error);
			window.messageApi.error("No se pudo guardar el borrador");
		} finally {
			setLoading(false);
		}
	};

	const handleConfirmationSubmit = async (confirmedValues, hasChanges) => {

		if (submittingRef.current) return;
		submittingRef.current = true;

		try {
			setLoading(true);

			if (hasChanges) {
				await updateClient(selectedClient.id, {
					...confirmedValues,
					pendingSync: 1
				});
			}

			const mainFormValues = form.getFieldsValue(true);

			// Combinar los datos originales del formulario con los datos confirmados
			const finalInvoiceData = {
				...mainFormValues, //Form principal con caja, dia, tpv
				...confirmedValues, // incluye todo (EInv*, contacto, campos únicos de cada país.)
				Bill_to_Name: confirmedValues.name ?? confirmedValues.Bill_to_Name,
				Bill_to_Address: confirmedValues.Direccion ?? confirmedValues.Bill_to_Address,
				Bill_to_City: confirmedValues.Poblacion ?? confirmedValues.Bill_to_City,
				Bill_to_Country_Region_Code:
					confirmedValues.Pais ?? confirmedValues.Bill_to_Country_Region_Code,
				VAT_Registration_No_:
					confirmedValues.Numero_Identificacion_fiscal ??
					confirmedValues.VAT_Registration_No_,
				Tipo_Documento_SrI:
					confirmedValues.Tipo_Documento_SrI ??
					mainFormValues.Tipo_Documento_SrI,
			};

			// Formatear datos de la factura
			const invoiceData = {
				...finalInvoiceData,
				Document_Type: finalInvoiceData.Document_Type || "Invoice",
				Posting_Date: finalInvoiceData.Posting_Date?.format("YYYY-MM-DD"),
				Document_Date: finalInvoiceData.Document_Date?.format("YYYY-MM-DD"),
				// Para abonos, asegurarse de guardar el tipo de documento corregido
				Applies_to_Doc_Type:
					finalInvoiceData.Document_Type === "Credit Memo"
						? "Invoice"
						: undefined,
				No: null,
				Pre_Assigned_No: null,
				Customer_No: selectedClient.No,
				isDraft: false,
			};

			const cleanInvoice = Object.fromEntries(
				Object.entries(invoiceData).map(([k, v]) => [k, v ?? null])
			);

			let savedInvoice;

			if (isInvoiceEditing) {
				// Si estamos editando un borrador, convertirlo a factura normal
				if (isDraft) await convertDraftToInvoice(invoice.id);

				// Actualizar factura existente
				savedInvoice = await updateInvoice(invoice.id, cleanInvoice);

				await syncPaymentLines({
					paymentLines,
					invoiceId: savedInvoice.id,
					invoiceNo: null, //me aseguro de borrar en caso de borrador
					documentType: savedInvoice.Document_Type,
					paymentMethodDiscount,
				});
			} else {
				// Crear nueva factura - el número se generará automáticamente
				savedInvoice = await addInvoice(cleanInvoice);

				await Promise.all(
					lines
						.filter(l => l.key !== "new" && l.No && l.Description)
						.map(line =>
							addInvoiceLine({
								...line,
								invoiceNo: null, //me aseguro de borrar en caso de ser Nota de credito
								invoiceId: savedInvoice.id,
								Document_Type: savedInvoice.Document_Type,
							})
						)
				);

				// Guardar líneas de pago
				await syncPaymentLines({
					paymentLines,
					invoiceId: savedInvoice.id,
					invoiceNo: null,
					documentType: savedInvoice.Document_Type,
					paymentMethodDiscount,
				});
			}

			setCreatedInvoice(savedInvoice); // tiene el id y otros datos generados para la impresion del ticket

			// Logica de contabilizacion segun pais
			const syncResult = await submitInvoiceAndPrepareFiscalTicket({
				invoice: savedInvoice,
				country
			});

			if (!syncResult.ok) {
				window.messageApi.warning(
					`Factura creada pero no contabilizada. ${syncResult.message}`,
					{ modal: true }
				);
				onSave(savedInvoice);
				return;
			}

			window.messageApi.success(
				isInvoiceEditing
					? "Factura actualizada correctamente"
					: "Factura creada correctamente"
			);

			setConfirmationModalVisible(false);
			setTicketModalVisible(true);

		} catch (error) {
			console.error(error);
			window.messageApi.error(
				error?.message || "No se pudo guardar la factura"
			);

		} finally {
			setLoading(false);
			submittingRef.current = false; // liberar el guard al finalizar
		}
	};


	// Funciones para líneas de pago
	const showAddPaymentLineModal = () => {
		if (isSynchronized) {
			window.messageApi.error(
				"No se pueden añadir pagos a una factura sincronizada con Business Central"
			);
			return;
		}

		const isAbono = form.getFieldValue("Document_Type") === "Credit Memo";

		// Si es una nota de crédito, no permitir añadir líneas de pago manualmente
		if (isAbono) {
			window.messageApi.error(
				"Las líneas de pago en notas de crédito se importan automáticamente de la factura original y no pueden modificarse manualmente"
			);
			return;
		}

		// Calcular el importe pendiente para pasarlo al formulario de pago
		// Si ya hay pagos existentes, usar el total original sin descuentos
		// porque al añadir un segundo pago se eliminará el descuento
		const totalToUse = paymentLines.length > 0 ? totalAjustado : total;

		const pendingAmount = totalToUse - paymentTotal;

		setSelectedPaymentLine({
			// Para todos los casos, el importe debe ser positivo
			pendingAmount: pendingAmount > 0 ? pendingAmount : 0,
			// Indicar si es un abono para que el formulario de pago lo maneje correctamente
			isAbono: false,
			// Indicar si ya hay líneas de pago existentes para prevenir descuentos
			hasExistingPayments: paymentLines.length > 0,
			// Pasar el número total de líneas de pago que existirán después de añadir esta
			totalPaymentLines: paymentLines.length + 1,
		});
		setPaymentLineModalVisible(true);
	};

	const showEditPaymentLineModal = (line) => {
		if (isSynchronized) {
			window.messageApi.error(
				"No se pueden editar pagos de una factura sincronizada con Business Central"
			);
			return;
		}

		const isAbono = form.getFieldValue("Document_Type") === "Credit Memo";

		// Si es una nota de crédito, no permitir editar líneas de pago
		if (isAbono) {
			window.messageApi.error(
				"Las líneas de pago en notas de crédito se importan automáticamente de la factura original y no pueden modificarse"
			);
			return;
		}

		setSelectedPaymentLine({
			...line,
			isAbono: false,
			// Indicar si hay otras líneas de pago además de esta
			hasExistingPayments: paymentLines.length > 1,
			// Pasar el número total de líneas de pago
			totalPaymentLines: paymentLines.length,
		});
		setPaymentLineModalVisible(true);
	};

	const handleAddPaymentLine = async (lineData) => {
		try {
			setPaymentLineLoading(true);

			if (isInvoiceEditing) {
				const savedLine = await addInvoicePaymentLine({
					...lineData,
					invoiceId: invoice.id,
					Document_Type: invoice.Document_Type || "Invoice",
					Document_No_: invoice?.No,
					Line_No: paymentLines.length + 1,
				});

				setPaymentLines([...paymentLines, savedLine]);
			} else {
				const newLine = {
					...lineData,
					id: `temp-${Date.now()}`,
					Line_No: paymentLines.length + 1,
				};
				setPaymentLines([...paymentLines, newLine]);
			}

			setPaymentLineModalVisible(false);
			window.messageApi.success("Línea de pago agregada correctamente");
		} catch (error) {
			window.messageApi.error("No se pudo agregar la línea de pago");
		} finally {
			setPaymentLineLoading(false);
		}
	};

	const handleUpdatePaymentLine = async (updatedLine) => {
		try {
			setPaymentLineLoading(true);

			const parsedId =
				typeof updatedLine?.id === "number"
					? updatedLine.id
					: typeof updatedLine?.id === "string"
						? Number(updatedLine.id)
						: NaN;
			const isPersistent = Number.isFinite(parsedId);
			const normalizedLine = isPersistent
				? { ...updatedLine, id: parsedId }
				: updatedLine;

			if (isInvoiceEditing && isPersistent) {
				await updateInvoicePaymentLine(parsedId, normalizedLine);
			}

			setPaymentLines((prev) =>
				prev.map((l) => (String(l.id) === String(updatedLine.id) ? normalizedLine : l))
			);

			setPaymentLineModalVisible(false);
			window.messageApi.success("Línea de pago actualizada correctamente");
		} catch (error) {
			window.messageApi.error("No se pudo actualizar la línea de pago");
		} finally {
			setPaymentLineLoading(false);
		}
	};

	const handleDeletePaymentLine = async (line) => {
		if (isSynchronized) return window.messageApi.error("Factura sincronizada");

		window.messageApi.confirm({
			title: "¿Está seguro de eliminar esta línea de pago?",
			content: "Esta acción no se puede deshacer",
			okText: "Eliminar",
			okType: "danger",
			cancelText: "Cancelar",
			onOk: async () => {
				try {
					setPaymentLineLoading(true);

					const parsedId =
						typeof line?.id === "number"
							? line.id
							: typeof line?.id === "string"
								? Number(line.id)
								: NaN;
					const isPersistent = Number.isFinite(parsedId);

					if (isInvoiceEditing && isPersistent) {
						await deleteInvoicePaymentLine(parsedId);
					}

					setPaymentLines((prev) =>
						prev.filter((l) => String(l.id) !== String(line.id))
					);

					window.messageApi.success("Línea de pago eliminada correctamente");
				} catch {
					window.messageApi.error("No se pudo eliminar la línea de pago");
				} finally {
					setPaymentLineLoading(false);
				}
			},
		});
	};

	const paymentColumns = [
		{
			title: "Línea",
			dataIndex: "Line_No",
			key: "Line_No",
			render: (_, __, index) => index + 1,
		},
		{
			title: "Forma de pago",
			dataIndex: "Payment_Method",
			key: "Payment_Method",
			render: (method, record, index, data) => {
				const hasMultiplePayments = paymentLines.length > 1;

				return (
					<div>
						<span>
							{record.isAdjustment ? (
								<span style={{ fontWeight: "bold" }}>
									Ajuste 4 %
								</span>
							) :
								method}
							{paymentMethodDiscount > 0 && (
								<Tag
									color={hasMultiplePayments ? "orange" : "green"}
									style={{ marginLeft: 8 }}
									title={
										hasMultiplePayments
											? "Este descuento no se aplica debido a múltiples formas de pago"
											: "Descuento aplicado"
									}
								>
									Dto: {record.Dto}%
									{!hasMultiplePayments &&
										` (${formatNumberByCountry(-(record.Dto_Amount || 0), country, true)})`}
									{hasMultiplePayments && " (No aplicado)"}
								</Tag>
							)}
							{record.isAutoGenerated && (
								<Tag color="blue" style={{ marginLeft: 8 }}>
									Automático
								</Tag>
							)}
						</span>
						{/* Mostrar información del cambio para pagos en efectivo */}
						{record.importeRecibido && record.cambio !== undefined && (
							<div style={{ marginTop: 4, fontSize: "12px", color: "#666" }}>
								<div>Recibido: {formatNumberByCountry(record.importeRecibido, country, true)} </div>
								{record.cambio > 0 && (
									<div style={{ color: "#52c41a", fontWeight: "bold" }}>
										Cambio: {formatNumberByCountry(record.cambio, country, true)}
									</div>
								)}
							</div>
						)}
					</div>
				);
			},
		},
		{
			title: "Importe",
			dataIndex: "Amount",
			key: "Amount",
			render: (_, record) => {
				const hasMultiplePayments = paymentLines.length > 1;
				if (hasMultiplePayments || paymentMethodDiscount == 0) {
					let amountCol = record.originalAmount ?? record.Amount ?? 0
					return `${formatNumberByCountry(amountCol, country, true)}`
				}
				else {
					return `${formatNumberByCountry(record.Amount || 0, country, true)}`
				}
			},
		},
		{
			title: "Acciones",
			key: "actions",
			render: (_, record) => {
				const isAbono = form.getFieldValue("Document_Type") === "Credit Memo";
				const isDisabled = isSynchronized || isAbono;

				return (
					<div className="line-actions">
						<Button
							icon={<EditOutlined />}
							size="small"
							onClick={() => showEditPaymentLineModal(record)}
							disabled={isDisabled}
							title={
								isSynchronized
									? "No se pueden editar pagos de facturas sincronizadas"
									: isAbono
										? "Las líneas de pago en notas de crédito se importan automáticamente y no pueden editarse"
										: "Editar pago"
							}
						/>
						<Button
							icon={<DeleteOutlined />}
							size="small"
							danger
							onClick={() => handleDeletePaymentLine(record)}
							disabled={isDisabled}
							title={
								isSynchronized
									? "No se pueden eliminar pagos de facturas sincronizadas"
									: isAbono
										? "Las líneas de pago en notas de crédito se importan automáticamente y no pueden eliminarse"
										: "Eliminar pago"
							}
						/>
					</div>
				);
			},
		},
	];

	return (
		<div className="invoice-form-container">
			<h2>
				{isInvoiceEditing
					? `Editar ${documentType === "Credit Memo" ? "nota de crédito" : "factura"
					}`
					: `Nueva ${documentType === "Credit Memo" ? "nota de crédito" : "factura"
					}`}
				{isDraft ? " (Borrador)" : ""}
			</h2>

			<Form
				form={form}
				layout="vertical"
				onFinish={handleSubmit}
				onFinishFailed={(err) => showAntdFormError(err)}
				scrollToFirstError
				initialValues={{
					Document_Type: "Invoice",
					Posting_Date: dayjs(),
					Document_Date: dayjs(),
				}}
			>
				<Card className="invoice-header-section">
					<InvoiceHeaderSlot
						documentType={documentType}
						cajaActiva={cajaActiva}
						aperturaActiva={aperturaActiva}
						turnoNombre={turnoNombre}
						permissions={permissions}
						hasKitsInLines={hasKitsInLines}
					/>

					<Divider orientation="left">Cliente</Divider>

					<ClientSectionSlot
						permissions={permissions}
						requiredFields={requiredFields}
						loading={loading}
						clients={clients}
						handleClientChange={handleClientChange}
						handleAddClient={handleAddClient}
						colegios={colegios}
						handleColegioChange={handleColegioChange}
						invoiceData={invoice}
						isNewClient={isNewClient}
						clientAddresses={clientAddresses}
						active={false}
						tpv={tpv}
					/>

					<BillingInfoSectionSlot selectedClient={selectedClient} tpv={tpv} />

					<Divider orientation="left">Cupón de descuento y Campaña</Divider>

					<Row gutter={16}>
						<Col xs={24} sm={16}>
							<Form.Item
								name="couponDetail"
								label="Detalle de cupón aplicado"
								tooltip="Seleccione un detalle de cupón válido para aplicar a esta factura"
							>
								<Select
									showSearch
									placeholder="Seleccionar detalle de cupón (opcional)"
									optionFilterProp="children"
									optionLabelProp="label"
									loading={loadingCoupons}
									onChange={handleCouponChange}
									allowClear
									disabled={isSynchronized}
									filterOption={(input, option) =>
										(option?.label || "")
											.toLowerCase()
											.includes(input.toLowerCase())
									}
								>
									{availableCouponDetails.map((couponDetail) => (
										<Select.Option
											key={couponDetail.key}
											value={couponDetail.key}
											disabled={!couponDetail.isValid}
											label={couponDetail.label}
										>
											<div
												style={{
													display: "flex",
													justifyContent: "space-between",
													alignItems: "center",
												}}
											>
												<span>
													{couponDetail.couponNo} -{" "}
													{couponDetail.couponDescription}
													<br />
													<small style={{ color: "#666" }}>
														{couponDetail.detailType}: {couponDetail.detailNo}
													</small>
												</span>
											</div>
											{!couponDetail.isValid && (
												<div
													style={{
														fontSize: "12px",
														color: "#ff4d4f",
														marginTop: "4px",
													}}
												>
													{toReadableText(
														couponDetail.validation?.detailValidation?.reason ||
														couponDetail.validation?.couponValidation?.reason,
														"Detalle no válido para esta factura"
													)}
												</div>
											)}
											{couponDetail.isValid && (
												<div
													style={{
														fontSize: "12px",
														color: "#52c41a",
														marginTop: "4px",
													}}
												>
													Válido para {couponDetail.detailType}:{" "}
													{couponDetail.detailNo}
												</div>
											)}
										</Select.Option>
									))}
								</Select>
							</Form.Item>
						</Col>
						<Col xs={24} sm={8}>
							{selectedCouponDetail && (
								<div
									style={{
										padding: "8px 12px",
										backgroundColor: "#f6ffed",
										border: "1px solid #b7eb8f",
										borderRadius: "6px",
										marginTop: "30px",
									}}
								>
									<div
										style={{
											display: "flex",
											alignItems: "center",
											gap: "8px",
										}}
									>
										<TagOutlined style={{ color: "#52c41a" }} />
										<span style={{ fontWeight: "bold", color: "#52c41a" }}>
											Detalle de cupón seleccionado
										</span>
									</div>
									<div
										style={{
											fontSize: "12px",
											color: "#666",
											marginTop: "4px",
										}}
									>
										<strong>Cupón:</strong>{" "}
										{toReadableText(selectedCouponDetail.couponNo, "sin número")} -{" "}
										{toReadableText(
											selectedCouponDetail.couponDescription,
											"sin descripción"
										)}
									</div>
									<div style={{ fontSize: "12px", color: "#666" }}>
										<strong>Detalle:</strong>{" "}
										{toReadableText(selectedCouponDetail.detailType)}:{" "}
										{toReadableText(selectedCouponDetail.detailNo, "Sin información")}
									</div>
								</div>
							)}
						</Col>
					</Row>

					<Row gutter={16}>
						<Col xs={24} sm={16}>
							<Form.Item
								name="Campaign_No_"
								label="Campaña aplicada"
								tooltip="Seleccione una campaña válida para aplicar a esta factura"
								rules={[{ required: requiredFields?.campaign, message: "Seleccione una campaña" }]}
							>
								<Select
									showSearch
									placeholder="Seleccionar campaña (opcional)"
									optionFilterProp="children"
									optionLabelProp="label"
									loading={loadingCampaigns}
									allowClear
									filterOption={(input, option) =>
										(option?.label || "")
											.toLowerCase()
											.includes(input.toLowerCase())
									}
								>
									{campaigns.map((campaign) => (
										<Select.Option
											key={campaign.No}
											value={campaign.No}
											label={`${campaign.No} - ${campaign.Description || "Sin descripción"
												}`}
										>
											<div
												style={{
													display: "flex",
													justifyContent: "space-between",
													alignItems: "center",
												}}
											>
												<span>
													{campaign.No} -{" "}
													{campaign.Description || "Sin descripción"}
												</span>
											</div>
										</Select.Option>
									))}
								</Select>
							</Form.Item>
						</Col>
						<Col xs={24} sm={8}>
							{selectedCampaignNo && (
								<div
									style={{
										padding: "8px 12px",
										backgroundColor: "#e6f7ff",
										border: "1px solid #91d5ff",
										borderRadius: "6px",
										marginTop: "30px",
									}}
								>
									<div
										style={{
											display: "flex",
											alignItems: "center",
											gap: "8px",
										}}
									>
										<TagOutlined style={{ color: "#1890ff" }} />
										<span style={{ fontWeight: "bold", color: "#1890ff" }}>
											Campaña seleccionada
										</span>
									</div>
									<div
										style={{
											fontSize: "12px",
											color: "#666",
											marginTop: "4px",
										}}
									>
										<strong>Campaña:</strong> {selectedCampaignNo}
									</div>
									{selectedCampaign?.Description && (
										<div style={{ fontSize: "12px", color: "#666" }}>
											<strong>Descripción:</strong>{" "}
											{selectedCampaign.Description}
										</div>
									)}
									{selectedCampaign &&
										(selectedCampaign.Starting_Date ||
											selectedCampaign.Ending_Date) && (
											<div style={{ fontSize: "12px", color: "#666" }}>
												<strong>Fechas:</strong>{" "}
												{selectedCampaign.Starting_Date || "Sin fecha inicio"} -{" "}
												{selectedCampaign.Ending_Date || "Sin fecha fin"}
											</div>
										)}
								</div>
							)}
						</Col>
					</Row>
				</Card>

				<Card className="invoice-lines-section">
					<h3>Líneas de factura</h3>

					<InvoiceLines
						invoice={invoice}
						lines={lines}
						setLines={setLines}
						products={products}
						isSynchronized={isSynchronized}
						isInvoiceEditing={isInvoiceEditing}
						selectedClientDiscount={selectedClientDiscount}
						loading={lineLoading}
						selectedClient={selectedClient}
						selectedColegio={form.getFieldValue("Colegio")}
						selectedCouponDetail={selectedCouponDetail}
						onPaymentLinesUpdate={setPaymentLines}
						documentType={documentType}
						totals={totals}
						country={country}
					/>
				</Card>

				<Card className="invoice-payments-section">
					<h3>Pagos</h3>

					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={showAddPaymentLineModal}
						className="add-payment-button"
						disabled={
							isSynchronized ||
							form.getFieldValue("Document_Type") === "Credit Memo"
						}
						title={
							isSynchronized
								? "No se pueden añadir pagos a facturas sincronizadas"
								: form.getFieldValue("Document_Type") === "Credit Memo"
									? "Las líneas de pago en notas de crédito se importan automáticamente"
									: "Añadir pago"
						}
					>
						Añadir pago
					</Button>

					<Table
						columns={paymentColumns}
						dataSource={ajuste > 0
							? [
								...paymentLines,
								{
									key: "ajuste",
									Payment_Method: "Ajuste 4%",
									Amount: -ajuste,
									isAdjustment: true,
								},
							]
							: paymentLines}
						rowKey="id"
						loading={paymentLineLoading}
						className="invoice-payment-lines-table"
						scroll={{ x: "max-content" }}
					/>

					<div className="invoice-totals">
						<div className="invoice-totals-item grand-total">
							<span>Total pagado:</span>
							<span>{formatNumberByCountry(totalAjustado, country, true)}</span>
						</div>
						{paymentMethodDiscount > 0 && (
							<div className="invoice-totals-item">
								<span>Descuento forma de pago:</span>
								<span>{paymentMethodDiscount}%</span>
							</div>
						)}
						{paymentLines.length > 1 && (
							<div className="invoice-totals-item">
								<span>Descuento forma de pago:</span>
								<span style={{ color: "#ff4d4f" }}>
									No aplicable con múltiples formas de pago
								</span>
							</div>
						)}
						<div className="invoice-totals-item grand-total">
							<span>Pendiente:</span>
							<span
								className={
									form.getFieldValue("Document_Type") === "Credit Memo" &&
										Math.abs(totalAjustado + ajuste) -
										paymentTotal <
										0
										? "negative-amount"
										: ""
								}
							>
								{form.getFieldValue("Document_Type") === "Credit Memo"
									? (
										formatNumberByCountry(
											(Math.abs(totalAjustado + ajuste) - paymentTotal),
											country,
											true,
										)
									)
									: (
										formatNumberByCountry(
											(totalAjustado - paymentTotal),
											country,
											true,
										)
									)
								}
							</span>
						</div>
					</div>
				</Card>

				<div className="invoice-form-actions">
					<Button onClick={onCancel}>Cancelar</Button>
					<Button
						icon={<SaveOutlined />}
						onClick={handleSaveAsDraft}
						loading={loading}
						disabled={
							isSynchronized ||
							(form.getFieldValue("Document_Type") === "Credit Memo" &&
								!isApproximatelyZero(
									Math.abs(totalAjustado + ajuste) -
									paymentTotal
								)
							)
						}
						title={
							isSynchronized
								? "No se pueden modificar facturas sincronizadas"
								: form.getFieldValue("Document_Type") === "Credit Memo" &&
									!isApproximatelyZero(
										Math.abs(totalAjustado + ajuste) -
										paymentTotal
									)
									? "Las notas de crédito requieren que el pendiente sea 0.00"
									: "Guardar como borrador de " +
									(documentType === "Credit Memo"
										? "nota de crédito"
										: "factura")
						}
					>
						Guardar como borrador de{" "}
						{documentType === "Credit Memo" ? "nota de crédito" : "factura"}
					</Button>
					<Button
						type="primary"
						htmlType="submit"
						loading={loading}
						disabled={
							(isSynchronized && form.getFieldValue("Document_Type") !== "Credit Memo") ||
							(form.getFieldValue("Document_Type") === "Credit Memo"
								? !isApproximatelyZero(
									Math.abs(totalAjustado + ajuste) -
									paymentTotal
								)
								: totalAjustado -
								paymentTotal >
								0)
						}
						title={
							isSynchronized
								? "No se pueden modificar facturas sincronizadas"
								: form.getFieldValue("Document_Type") === "Credit Memo"
									? !isApproximatelyZero(
										Math.abs(totalAjustado + ajuste) -
										paymentTotal
									)
										? "Las notas de crédito requieren que el pendiente sea 0.00"
										: ""
									: totalAjustado -
										paymentTotal >
										0
										? "Debe completar todos los pagos antes de finalizar"
										: ""
						}
					>
						{isInvoiceEditing
							? isDraft
								? "Finalizar y guardar"
								: "Actualizar " +
								(documentType === "Credit Memo"
									? "nota de crédito"
									: "factura")
							: "Crear " +
							(documentType === "Credit Memo"
								? "nota de crédito"
								: "factura")}
					</Button>
				</div>
			</Form>

			{/* Modal de confirmación de datos del cliente (requerido por ley) */}
			<Modal
				title={`Confirmar datos de ${confirmationData?.Document_Type === "Credit Memo"
					? "nota de crédito"
					: "facturación"
					}`}
				open={confirmationModalVisible}
				onCancel={closeConfirmationModal}
				footer={null}
				width={700}
				destroyOnHidden
			>
				{confirmationClientData && (
					<ConfirmationSectionSlot
						initialData={confirmationClientData}
						handleSubmit={handleConfirmationSubmit}
						handleCancel={closeConfirmationModal}
						handleError={setError}
					/>
				)}
			</Modal>

			<Modal
				title={selectedPaymentLine ? "Editar pago" : "Añadir pago"}
				open={paymentLineModalVisible}
				onCancel={() => setPaymentLineModalVisible(false)}
				footer={null}
				width={700}
				destroyOnHidden
			>
				<InvoicePaymentLineForm
					line={selectedPaymentLine}
					hasSpecialDiscount={hasSpecialDiscount}
					onAddLine={handleAddPaymentLine}
					onUpdateLine={handleUpdatePaymentLine}
					onCancel={() => setPaymentLineModalVisible(false)}
					country={country}
				/>
			</Modal>

			{/* Modal del ticket */}
			<TicketSectionSlot
				open={ticketModalVisible}
				onClose={() => {
					setTicketModalVisible(false);
					// Al cerrar el ticket, notificar al componente padre
					onSave(createdInvoice);
				}}
				invoice={createdInvoice}
				lines={lines}
				caja={createdInvoice?.caja || form.getFieldValue("caja")}
				turno={createdInvoice?.turno || form.getFieldValue("turno")}
				paymentLines={paymentLines}
				selectedClient={selectedClient}
				tpvNo={tpv?.No}
				country={country}
			/>

			{/* Modal para crear nuevo cliente */}
			<Modal
				title="Nuevo Cliente"
				open={clientModalVisible}
				onCancel={() => setClientModalVisible(false)}
				footer={null}
				width={800}
			>
				<ClientForm
					loadingForm={loadingClientForm}
					isCreate={true}
					error={error}
					handleSubmit={handleSubmitNewClient}
					handleCancel={handleCancelNewClientForm}
					handleError={setError}
				/>
			</Modal>
		</div>
	);
};

export default InvoiceForm;
